import { useQuery } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { Promotion, PromotionStatus } from "@/types/sms";

export const PROMOTIONS_QUERY_KEY = ["sms-promotions"] as const;

interface PromotionRow extends Omit<Promotion, "channels"> {
  sms_promotion_channels: Promotion["channels"];
}

/**
 * Reads the signed-in store's promotions joined with their channels.
 * sms_promotions / sms_promotion_channels are not in the generated Supabase
 * Database types yet, so this goes through an untyped client (same pattern
 * as usePosStores / useMarketingSettings).
 */
export const usePromotions = (params: { status: PromotionStatus; search?: string }) => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<Promotion[]>({
    queryKey: [...PROMOTIONS_QUERY_KEY, params],
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      let query = sb
        .from("sms_promotions")
        .select("*, sms_promotion_channels(*)")
        .eq("status", params.status)
        .order("created_at", { ascending: false });

      if (params.search?.trim()) {
        query = query.ilike("title", `%${params.search.trim()}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return ((data ?? []) as PromotionRow[]).map((row) => {
        const { sms_promotion_channels, ...rest } = row;
        return { ...rest, channels: sms_promotion_channels ?? [] } as Promotion;
      });
    },
    staleTime: 60 * 1000,
  });
};
